// src/components/reports/VaccineInventoryReport.js
import React, { useState, useEffect } from "react";
import {
  Box,
  Heading,
  FormControl,
  FormLabel,
  Input,
  Select,
  VStack,
  HStack,
  Spacer,
  FormErrorMessage,
} from "@chakra-ui/react";
import DataTable from "../AppDataTable";
import { tableCustomStyles } from "../../themes/TableStyles";
import apiService from "../../services/ApiService";
import { useAuth } from "../../context/AuthContext";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import { autoTable } from "jspdf-autotable";
import VaccineInventoryChart from "./VaccineInventoryChart";
import {
  Search,
  ExportExcel,
  ExportPdf,
  Print,
  Clear,
} from "../../themes/IconButtonSet";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSearch, faTable } from "@fortawesome/free-solid-svg-icons";
import { StandardPageTitle, StandardHeader } from "../../themes/StandardHeader";

// ฟังก์ชันโหลดฟอนต์ Sarabun
const loadSarabunFont = async () => {
  try {
    const fontResponse = await fetch("/fonts/THSarabunNew.ttf");
    if (!fontResponse.ok) {
      throw new Error(
        `Failed to fetch Sarabun font: ${fontResponse.statusText}`
      );
    }
    const fontArrayBuffer = await fontResponse.arrayBuffer();
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64String = reader.result
          .replace("data:application/octet-stream;base64,", "")
          .replace("data:font/ttf;base64,", "");
        resolve(base64String);
      };
      reader.onerror = reject;
      reader.readAsDataURL(new Blob([fontArrayBuffer]));
    });
  } catch (error) {
    console.error("Error loading Sarabun font:", error);
    return null;
  }
};

const VaccineInventoryReport = () => {
  const { sessionId } = useAuth();
  const [filterData, setFilterData] = useState({
    VaccineID: "",
    WarehouseID: "",
    LotID: "",
    ProductionDate: "",
    ExpiryDate: "",
    ExpiryDays: "",
    LivestockTypeID: "",
  });
  const [vaccineOptions, setVaccineOptions] = useState([]);
  const [warehouseOptions, setWarehouseOptions] = useState([]);
  const [livestockTypeOptions, setLivestockTypeOptions] = useState([]);
  const [inventoryData, setInventoryData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({});

  // ดึงข้อมูล Vaccine List
  const fetchVaccineOptions = async () => {
    try {
      const response = await apiService({
        method: "POST",
        url: "/getVaccinesList",
        data: {},
        sessionId: sessionId || "",
      });
      if (response.result === "success") {
        setVaccineOptions(response.VaccinesList || []);
      } else {
        console.error("Failed to fetch vaccines:", response.error);
      }
    } catch (error) {
      console.error("Error fetching vaccines:", error);
    }
  };

  // ดึงข้อมูล Warehouse List
  const fetchWarehouseOptions = async () => {
    try {
      const response = await apiService({
        method: "POST",
        url: "/getVaccineChildWarehousesList",
        data: {},
        sessionId: sessionId || "",
      });
      console.log("Warehouses Response:", response);
      if (response.result === "success") {
        const warehouseList = response.VaccineWarehousesList || [];
        setWarehouseOptions(warehouseList);
        // Set default WarehouseID to the first warehouse, if available
        if (warehouseList.length > 0) {
          setFilterData((prev) => ({
            ...prev,
            WarehouseID: warehouseList[0].WarehouseID,
          }));
          console.log(
            "Default WarehouseID set to:",
            warehouseList[0].WarehouseID
          );
        }
      } else {
        console.error("Failed to fetch warehouses:", response.error);
      }
    } catch (error) {
      console.error("Error fetching warehouses:", error);
    }
  };

  // ดึงข้อมูล Livestock Type List
  const fetchLivestockTypeOptions = async () => {
    try {
      const response = await apiService({
        method: "POST",
        url: "/getLivestockTypeList",
        data: {},
        sessionId: sessionId || "",
      });
      if (response.result === "success") {
        const types = response.LivestockTypeList || [];
        setLivestockTypeOptions(types);
        if (types.length === 0) {
          console.warn("LivestockTypeList is empty");
        }
      } else {
        console.error("Failed to fetch livestock types:", response.error);
      }
    } catch (error) {
      console.error("Error fetching livestock types:", error);
    }
  };

  // เรียก API getVaccineInventorySummary
  const fetchInventoryReport = async () => {
    if (!filterData.WarehouseID) {
      alert("กรุณาเลือกคลังวัคซีน");
      return;
    }
    setIsLoading(true);
    try {
      const response = await apiService({
        method: "POST",
        url: "/getVaccineInventoryByWarehouseSummary",
        data: {
          VaccineID: filterData.VaccineID || "",
          WarehouseID: filterData.WarehouseID,
          LotID: filterData.LotID || "",
          ProductionDate: filterData.ProductionDate || "",
          ExpiryDate: filterData.ExpiryDate || "",
          ExpiryDays: filterData.ExpiryDays || "",
          LivestockTypeID: filterData.LivestockTypeID || "",
        },
        sessionId: sessionId || "",
      });
      if (response.result === "success") {
        setInventoryData(response.InventoryReport || []);
      } else {
        setInventoryData([]);
        alert("ไม่พบข้อมูล: " + response.error);
      }
    } catch (error) {
      console.error("Error fetching inventory:", error);
      alert("เกิดข้อผิดพลาดในการดึงข้อมูล");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (sessionId) {
      fetchVaccineOptions();
      fetchWarehouseOptions();
      fetchLivestockTypeOptions();
    }
  }, [sessionId]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFilterData({ ...filterData, [name]: value });
  };

  const columns = [
    {
      name: (
        <HStack>
          <FontAwesomeIcon icon={faTable} />
          <span>ชื่อวัคซีน (TH)</span>
        </HStack>
      ),
      selector: (row) => row.VaccineNameTH,
      sortable: true,
    },
    {
      name: (
        <HStack>
          <FontAwesomeIcon icon={faTable} />
          <span>จำนวนคงคลัง</span>
        </HStack>
      ),
      selector: (row) => Number(row.TotalQuantity)?.toLocaleString(),
      sortable: true,
      width: "200px",
    },
    {
      name: (
        <HStack>
          <FontAwesomeIcon icon={faTable} />
          <span>ประเภทปศุสัตว์</span>
        </HStack>
      ),
      selector: (row) => row.LivestockType,
      sortable: true,
      width: "200px",
    },
    {
      name: (
        <HStack>
          <FontAwesomeIcon icon={faTable} />
          <span>ชื่อคลัง</span>
        </HStack>
      ),
      selector: (row) => row.WarehouseName,
      sortable: true,
    },
    {
      name: (
        <HStack>
          <FontAwesomeIcon icon={faTable} />
          <span>ชื่อวัคซีน (EN)</span>
        </HStack>
      ),
      selector: (row) => row.VaccineNameEN,
      sortable: true,
    },
  ];

  const exportToExcel = () => {
    const wsData = inventoryData.map((item) => ({
      "ชื่อวัคซีน (TH)": item.VaccineNameTH,
      จำนวนคงคลัง: item.TotalQuantity,
      ประเภทปศุสัตว์: item.LivestockType,
      ชื่อคลัง: item.WarehouseName,
      "ชื่อวัคซีน (EN)": item.VaccineNameEN,
    }));
    const ws = XLSX.utils.json_to_sheet(wsData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "InventoryReport");
    XLSX.writeFile(wb, "VaccineInventoryReport.xlsx");
  };

  const exportToPDF = async () => {
    const doc = new jsPDF({ orientation: "landscape" });
    const fontBase64 = await loadSarabunFont();
    if (fontBase64) {
      doc.addFileToVFS("THSarabunNew.ttf", fontBase64);
      doc.addFont("THSarabunNew.ttf", "Sarabun", "normal");
      doc.setFont("Sarabun");
    } else {
      console.warn("Failed to load Sarabun font, using default font");
    }

    doc.setFontSize(16);
    doc.text("รายงานวัคซีนคงคลัง", 12, 10);
    doc.setFontSize(14);
    doc.text(
      `คลังวัคซีน: ${
        warehouseOptions.find((w) => w.WarehouseID === filterData.WarehouseID)
          ?.WarehouseName || "ไม่ระบุ"
      }`,
      14,
      18
    );
    doc.text(`วันที่: ${new Date().toLocaleDateString("th-TH")}`, 14, 26);

    autoTable(doc, {
      startY: 34,
      head: [
        [
          "ชื่อวัคซีน (TH)",
          "จำนวนคงคลัง",
          "ประเภทปศุสัตว์",
          "ชื่อคลัง",
          "ชื่อวัคซีน (EN)",
        ],
      ],
      body: inventoryData.map((item) => [
        item.VaccineNameTH,
        item.TotalQuantity,
        item.LivestockType,
        item.WarehouseName,
        item.VaccineNameEN,
      ]),
      styles: {
        font: fontBase64 ? "Sarabun" : "helvetica",
        fontSize: 11,
        cellPadding: 1,
        lineWidth: 0.1,
        lineColor: [0, 0, 0],
        fillColor: [255, 255, 255],
      },      
      headStyles: {
        font: fontBase64 ? "Sarabun" : "helvetica",
        fontStyle: "normal",
        fontSize: 11,
        halign: "center",
        fillColor: [211, 211, 211],
        textColor: "black",
        lineWidth: 0.1,
        lineColor: [0, 0, 0],
      },
      alternateRowStyles: {
        fillColor: [255, 255, 255],
      },
      columnStyles: {
        0: { cellWidth: 70 },
        1: { cellWidth: 30 },
        2: { cellWidth: 30 },
        3: { cellWidth: 30 },
        4: { cellWidth: 70 },
      },
      didParseCell: (data) => {
        data.cell.styles.cellWidth = "auto";
      },
    });

    doc.save("VaccineInventoryReport.pdf");
  };

  const printTable = () => {
    const printContent = `
      <html>
        <head>
          <title>Vaccine Inventory Report</title>
          <style>
            table { border-collapse: collapse; width: 100%; }
            th, td { border: 1px solid black; padding: 8px; text-align: left; }
            th { background-color: #f2f2f2; }
          </style>
        </head>
        <body>
          <h2>รายงานวัคซีนคงคลัง</h2>
          <p>คลังวัคซีน: ${
            warehouseOptions.find(
              (w) => w.WarehouseID === filterData.WarehouseID
            )?.WarehouseName || "ไม่ระบุ"
          }</p>
          <p>วันที่: ${new Date().toLocaleDateString("th-TH")}</p>
          <table>
            <thead>
              <tr>
                <th>ชื่อวัคซีน (TH)</th>
                <th>จำนวนคงคลัง</th>
                <th>ประเภทปศุสัตว์</th>
                <th>ชื่อคลัง</th>
                <th>ชื่อวัคซีน (EN)</th>
              </tr>
            </thead>
            <tbody>
              ${inventoryData
                .map(
                  (item) => `
                    <tr>
                      <td>${item.VaccineNameTH}</td>
                      <td>${item.TotalQuantity}</td>
                      <td>${item.LivestockType}</td>
                      <td>${item.WarehouseName}</td>
                      <td>${item.VaccineNameEN}</td>
                    </tr>
                  `
                )
                .join("")}
            </tbody>
          </table>
        </body>
      </html>
    `;
    const newWindow = window.open("", "", "width=800,height=600");
    newWindow.document.write(printContent);
    newWindow.document.close();
    newWindow.print();
  };

  return (
    <Box p={35} m={30} borderWidth={1} borderRadius="lg" boxShadow="lg">
      <StandardPageTitle title="รายงานวัคซีนคงคลัง" />

      {/* ฟอร์มฟิลเตอร์ */}
      <Box borderWidth={1} borderRadius="lg" boxShadow="lg" p={5} mt={5} mb={6}>
        <StandardHeader
          title="ค้นหาข้อมูลวัคซีนคงคลัง"
          bgColor="iBlue.600"
          icon={faSearch}
        />
        <VStack spacing={6} p={5}>
          <HStack spacing={6} w="100%" flexWrap="wrap">
            <FormControl minW="200px" flex="1" isInvalid={errors.WarehouseID}>
              <FormLabel fontSize="xl" color="gray">
                คลัง
              </FormLabel>
              <Select
                name="WarehouseID"
                value={filterData.WarehouseID}
                onChange={handleInputChange}
                placeholder={
                  warehouseOptions.length === 0
                    ? "ไม่มีคลังวัคซีน"
                    : "เลือกคลังวัคซีน"
                }
                colorScheme="iBlue"
              >
                {warehouseOptions.map((warehouse) => (
                  <option
                    key={warehouse.WarehouseID}
                    value={warehouse.WarehouseID}
                  >
                    {warehouse.WarehouseName}
                  </option>
                ))}
              </Select>
              <FormErrorMessage>{errors.WarehouseID}</FormErrorMessage>
            </FormControl>
            <FormControl minW="200px" flex="1">
              <FormLabel fontSize="xl" color="gray">
                ประเภทปศุสัตว์
              </FormLabel>
              <Select
                name="LivestockTypeID"
                value={filterData.LivestockTypeID}
                onChange={handleInputChange}
                placeholder="เลือกประเภทปศุสัตว์ (ทั้งหมด)"
                colorScheme="iBlue"
              >
                {livestockTypeOptions.map((type) => (
                  <option
                    key={type.LivestockTypeID}
                    value={type.LivestockTypeID}
                  >
                    {type.LivestockTypeName}
                  </option>
                ))}
              </Select>
            </FormControl>
            <FormControl minW="200px" flex="1">
              <FormLabel fontSize="xl" color="gray">
                วัคซีน
              </FormLabel>
              <Select
                name="VaccineID"
                value={filterData.VaccineID}
                onChange={handleInputChange}
                placeholder="เลือกวัคซีน (ทั้งหมด)"
                colorScheme="iBlue"
              >
                {vaccineOptions.map((vaccine) => (
                  <option key={vaccine.VaccineID} value={vaccine.VaccineID}>
                    {vaccine.VaccineNameTH}
                  </option>
                ))}
              </Select>
            </FormControl>
            <FormControl minW="200px" flex="1">
              <FormLabel fontSize="xl" color="gray">
                Lot No.
              </FormLabel>
              <Input
                name="LotID"
                value={filterData.LotID}
                onChange={handleInputChange}
                placeholder="กรอก Lot No. (ถ้ามี)"
                colorScheme="iBlue"
              />
            </FormControl>
            <FormControl minW="200px" flex="1">
              <FormLabel fontSize="xl" color="gray">
                อายุคงเหลือ น้อยกว่า (วัน)
              </FormLabel>
              <Input
                name="ExpiryDays"
                value={filterData.ExpiryDays}
                onChange={handleInputChange}
                placeholder="กรอกจำนวนวัน (ถ้ามี)"
                colorScheme="iBlue"
              />
            </FormControl>
          </HStack>
          <Box w="100%" textAlign="right">
            <HStack spacing={5} justifyContent="flex-end">
              <Clear
                onClick={() =>
                  setFilterData({
                    VaccineID: "",
                    WarehouseID:
                      warehouseOptions.length > 0
                        ? warehouseOptions[0].WarehouseID
                        : "",
                    LotID: "",
                    ProductionDate: "",
                    ExpiryDate: "",
                    ExpiryDays: "",
                    LivestockTypeID: "",
                  })
                }
              />
              <Search onClick={fetchInventoryReport} isLoading={isLoading} />
            </HStack>
          </Box>
        </VStack>
      </Box>

      {/* ตาราง */}
      {inventoryData.length > 0 && (
        <Box borderWidth={1} borderRadius="lg" boxShadow="lg" p={5} mb={6}>
          <StandardHeader
            title="รายการวัคซีนคงคลัง"
            bgColor="iOrange.500"
            icon={faTable}
          />
          <HStack spacing={5} m={5}>
            <Spacer />
            <ExportExcel onClick={exportToExcel} />
            <ExportPdf onClick={exportToPDF} />
            <Print onClick={printTable} />
          </HStack>
          <Box p={5} overflowX="auto">
            <DataTable
              columns={columns}
              data={inventoryData}
              pagination
              highlightOnHover
              striped
              responsive
              customStyles={tableCustomStyles}
            />
          </Box>
        </Box>
      )}

      {/* กราฟ */}
      {inventoryData.length > 0 && (
        <VaccineInventoryChart
          data={inventoryData}
          warehouseOptions={warehouseOptions}
        />
      )}
    </Box>
  );
};

export default VaccineInventoryReport;
